// Translates the rendered page in place (text nodes + a few attributes) using the cached site dictionary.
// Only nodeValue/attributes are changed, so React keeps owning the DOM. Mark content with translate="no" to skip it.
import api from "@/lib/api";

const LANG = "es";
const CACHE = `gcw_i18n_${LANG}`;
const ATTRS = ["placeholder", "title", "aria-label", "alt"];
const BLOCK = 'script,style,noscript,code,pre,textarea,svg,[translate="no"],[data-no-translate],[contenteditable="true"],[contenteditable=""]';
const NUM = /\d+(?:[.,:]\d+)*/g;

const dict = new Map();
const pending = new Set();
const waiting = new Set();
const textState = new WeakMap();
const attrState = new WeakMap();
let timer = null;
let inflight = 0;
let enabled = true;

const translatable = (s) => s.length <= 2000 && /[A-Za-z]{2,}/.test(s) && !/^\S+@\S+\.\S+$/.test(s) && !/^(https?:\/\/|www\.)/i.test(s);

function lookup(raw) {
  const core = raw.trim();
  if (!core || !translatable(core)) return undefined;
  const nums = [];
  const tpl = core.replace(NUM, (m) => `{${nums.push(m) - 1}}`);
  const es = dict.get(tpl);
  if (es == null) {
    pending.add(tpl);
    return null;
  }
  const lead = raw.slice(0, raw.length - raw.trimStart().length);
  const trail = raw.slice(raw.trimEnd().length);
  return lead + es.replace(/\{(\d+)\}/g, (m, i) => nums[i] ?? m) + trail;
}

function doText(node, force) {
  let v = node.nodeValue;
  const st = textState.get(node);
  if (st && st.out === v) {
    if (!force || !st.src) return;
    v = st.src;
  }
  const out = lookup(v);
  if (out === undefined) return;
  if (out === null) { textState.set(node, { src: v, out: null }); waiting.add(node); return; }
  textState.set(node, { src: v, out });
  if (out !== node.nodeValue) node.nodeValue = out;
}

function doAttrs(el, force) {
  let m = attrState.get(el);
  for (const a of ATTRS) {
    let v = el.getAttribute(a);
    if (v == null) continue;
    if (m && m[a] && m[a].out === v) {
      if (!force) continue;
      v = m[a].src;
    }
    const out = lookup(v);
    if (out === undefined) continue;
    if (!m) { m = {}; attrState.set(el, m); }
    if (out === null) { waiting.add(el); continue; }
    m[a] = { src: v, out };
    if (out !== el.getAttribute(a)) el.setAttribute(a, out);
  }
}

const blocked = (el) => !el || !!el.closest(BLOCK);

function scan(root, force = false) {
  if (root.nodeType === 3) { if (!blocked(root.parentElement)) doText(root, force); return; }
  if (root.nodeType !== 1 || blocked(root)) return;
  doAttrs(root, force);
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
    acceptNode: (n) => (n.nodeType === 3 ? NodeFilter.FILTER_ACCEPT : n.matches(BLOCK) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_SKIP),
  });
  const els = [];
  for (let n = walker.nextNode(); n; n = walker.nextNode()) doText(n, force);
  root.querySelectorAll("[placeholder],[title],[aria-label],[alt]").forEach((el) => { if (!blocked(el)) els.push(el); });
  els.forEach((el) => doAttrs(el, force));
  schedule();
}

function schedule() {
  if (!pending.size || timer) return;
  timer = setTimeout(flush, 40);
}

async function flush() {
  timer = null;
  if (inflight >= 3) { timer = setTimeout(flush, 300); return; }
  const batch = [...pending].slice(0, 100);
  batch.forEach((s) => pending.delete(s));
  if (!batch.length) return;
  inflight += 1;
  try {
    const { data } = await api.post(`/i18n/${LANG}/translate`, { strings: batch });
    Object.entries(data.translations || {}).forEach(([k, v]) => dict.set(k, v));
    batch.forEach((s) => { if (!dict.has(s)) dict.set(s, s); });
    saveCache();
    retry();
  } catch {
    // leave English in place; strings retry on next page view
  } finally {
    inflight -= 1;
    if (pending.size) schedule();
  }
}

function retry() {
  const nodes = [...waiting];
  waiting.clear();
  nodes.forEach((n) => {
    if (!n.isConnected) return;
    if (n.nodeType === 3) { if (!blocked(n.parentElement)) doText(n); } else if (!blocked(n)) doAttrs(n);
  });
  schedule();
}

let saveT = null;
function saveCache() {
  clearTimeout(saveT);
  saveT = setTimeout(() => {
    try { localStorage.setItem(CACHE, JSON.stringify(Object.fromEntries(dict))); } catch { /* storage full */ }
  }, 800);
}

function translateTitle() {
  const t = document.querySelector("head > title");
  if (t?.firstChild) doText(t.firstChild);
}

export function setTranslatorEnabled(v) { enabled = v; }

// Resolve one string through the site dictionary (AI-translated and cached on first use).
export async function translateText(s) {
  const core = (s || "").trim();
  if (!core || !translatable(core)) return s;
  if (dict.has(core)) return dict.get(core);
  try {
    const { data } = await api.post(`/i18n/${LANG}/translate`, { strings: [core] });
    const out = data.translations?.[core];
    if (out) { dict.set(core, out); saveCache(); }
    return out || s;
  } catch {
    return s;
  }
}

export function startTranslator() {
  try { Object.entries(JSON.parse(localStorage.getItem(CACHE) || "{}")).forEach(([k, v]) => dict.set(k, v)); } catch { /* bad cache */ }
  const cold = dict.size === 0;
  if (cold) document.documentElement.classList.add("i18n-wait");
  const ready = () => document.documentElement.classList.remove("i18n-wait");
  setTimeout(ready, 2500);

  const obs = new MutationObserver((records) => {
    if (!enabled) return;
    for (const r of records) {
      if (r.type === "characterData") { if (!blocked(r.target.parentElement)) doText(r.target); }
      else if (r.type === "attributes") { if (!blocked(r.target)) doAttrs(r.target); }
      else r.addedNodes.forEach(scan);
    }
    schedule();
  });
  const begin = () => {
    obs.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRS });
    new MutationObserver(translateTitle).observe(document.head, { childList: true, subtree: true, characterData: true });
    scan(document.body);
    translateTitle();
  };
  if (document.body) begin(); else document.addEventListener("DOMContentLoaded", begin);

  api.get(`/i18n/${LANG}`).then(({ data }) => {
    dict.clear();
    Object.entries(data.strings || {}).forEach(([k, v]) => dict.set(k, v));
    saveCache();
    retry();
    scan(document.body, true);
    const t = document.querySelector("head > title");
    if (t?.firstChild) doText(t.firstChild, true);
  }).catch(() => {}).finally(() => setTimeout(ready, cold ? 300 : 0));
}
